import {useCallback, useEffect, useMemo, useState} from 'react'
import {getFileS3Url} from '../../core/_requests'
import {shareFileS3Url} from '../../core/storage'
import {getLogger} from '@/shared/utils'
import {useCognitoAuth} from '@/app/modules/auth'
import {CustomModal} from '../CustomModal'
import {FileTable, PickedFile} from '../FileTable'
import {parsePickedUri} from './storageUri'
import {
  createFilePickCancelledReply,
  createFilePickedManyReply,
  createFilePickedReply,
  FilePickRequest,
  isFilePickRequest,
} from './filePickMessages'
import './file-picker.scss'

/**
 * The page's one file-pick dialog.
 *
 * A wizard inside a viewer iframe cannot browse storage, so it posts `requestFilePick` and this
 * answers with a storage key. Mounted once, high enough to be inside the auth and React Query
 * providers, and independent of the viewer managers: it replies to `event.source`, so it does not
 * care which iframe asked or which manager owns it. (The managers dispatch on `action in this`, so
 * they ignore these messages.)
 *
 * The body is the **same `FileTable`** the Files panel renders, in pick mode. That is what makes the
 * dialog share the panel's React Query cache (no second listing request) and its websocket
 * invalidations, while keeping its own folder: `folderPath` is component state, so the two instances
 * navigate independently. It deliberately persists nothing — the folder it opens at comes from the
 * widget's current value, not from wherever the panel was last left.
 *
 * **One at a time.** A file dialog is a one-of-a-kind thing: two viewers must not both be driving
 * one. A request arriving while the dialog is open is queued and served when the first is answered,
 * and the waiting widget shows a pending state until then. Requests are never dropped and never
 * silently coalesced — each gets exactly one reply, so no widget is left waiting forever.
 */

/**
 * What the widget is told about a picked file. A file of My Files is named as `/metadata` names it;
 * one in a folder someone shares with the user is named by its key in the owner's space (E1), the
 * same string for every member, with `key` relative to the owner's space to match.
 */
export async function pickedReplyValue(
  file: PickedFile
): Promise<{uri: string; key: string; name: string}> {
  if (file.share) {
    return {
      uri: shareFileS3Url(file.share, file.key),
      key: `${file.share.folderPath}${file.key}`,
      name: file.name,
    }
  }
  return {uri: await getFileS3Url(file.key), key: file.key, name: file.name}
}

type PendingRequest = {
  reqId: string
  source: MessageEventSource
  filters?: string[]
  title?: string
  /** The widget's current value, if its build sends one. See `filePickMessages`. */
  currentUri?: string
  /** Whether the widget asked for several files. Absent from an older widget's request. */
  multiple?: boolean
}

export function FilePickerHost() {
  const [queue, setQueue] = useState<PendingRequest[]>([])
  // A list in both modes, so there is one shape to reason about; single selection is a list of one.
  const [selected, setSelected] = useState<PickedFile[]>([])
  const {currentUser} = useCognitoAuth()

  const active = queue[0]

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!isFilePickRequest(event.data) || !event.source) {
        return
      }

      const request = event.data as FilePickRequest

      setQueue((prev) => {
        // A widget that re-asks (a remount, a double click on Browse) must not queue twice: it is
        // still waiting on one reply, and the second would arrive with nothing listening.
        if (prev.some((entry) => entry.reqId === request.reqId)) {
          return prev
        }

        return [
          ...prev,
          {
            reqId: request.reqId,
            source: event.source!,
            filters: request.value?.filters,
            title: request.value?.title,
            currentUri: request.value?.currentUri,
            multiple: request.value?.multiple,
          },
        ]
      })
    }

    window.addEventListener('message', onMessage)

    return () => window.removeEventListener('message', onMessage)
  }, [])

  /**
   * Answer the active request and move on. Every path out of the dialog goes through here, so every
   * request gets exactly one reply.
   *
   * Resolving the URI is async — it needs the Cognito identity — so the queue is only advanced once
   * the reply has actually been posted. Cancelling needs no URI and takes the fast path.
   */
  const answer = useCallback(
    async (files: PickedFile[]) => {
      const current = queue[0]
      if (!current) {
        return
      }

      let reply = createFilePickCancelledReply(current.reqId)

      if (files.length) {
        try {
          /*
            The same resolver `/metadata` uses, so a picked file and an opened file are named
            identically: s3://<bucket>/<poolId>/<key>.

            All or nothing. A partial list would silently drop files the user had chosen and there is
            nowhere in this protocol to say which, so one failure answers with a cancel — the same
            reasoning as the single-file case, which keeps the widget's previous value rather than
            handing it something unusable.
          */
          const resolved = await Promise.all(files.map(pickedReplyValue))

          reply = current.multiple
            ? createFilePickedManyReply(current.reqId, resolved)
            : createFilePickedReply(current.reqId, resolved[0].uri, resolved[0].key, resolved[0].name)
        } catch (e) {
          getLogger().error('Could not resolve a storage URI for the picked files', e)
        }
      }

      // '*' as everywhere else in this bridge: the viewer iframes are served from their own origins,
      // and the payload is a storage URI scoped to the caller's own prefix, not a credential.
      try {
        ;(current.source as Window).postMessage(reply, '*')
      } catch (e) {
        // A closed or navigated-away iframe: nothing to tell, just carry on with the queue.
      }

      setQueue((prev) => prev.filter((pending) => pending.reqId !== current.reqId))
      setSelected([])
    },
    [queue]
  )

  // Clear the highlight when the dialog moves on to the next request in the queue.
  useEffect(() => setSelected([]), [active?.reqId])

  // Where to open, and what to preselect. Absent or foreign values fall back to the root, which is
  // also what every viewer build older than the `currentUri` field gets.
  const initial = useMemo(
    () => parsePickedUri(active?.currentUri, currentUser?.identity_id),
    [active?.currentUri, currentUser?.identity_id]
  )

  const pickOptions = useMemo(
    () => ({
      filters: active?.filters,
      initialSelectedKey: initial.key,
      multiple: active?.multiple,
      onSelect: (file: PickedFile | undefined) => setSelected(file ? [file] : []),
      onSelectMany: setSelected,
      // Not reached in multiple mode: a double-click there toggles a row instead of answering.
      onConfirm: (file: PickedFile) => void answer([file]),
    }),
    // `answer` closes over the queue, so it changes as the queue drains; that is fine here because
    // FileTable reads these through a ref rather than re-rendering on them.
    [active?.filters, active?.multiple, initial.key, answer]
  )

  if (!active) {
    return null
  }

  const waiting = queue.length - 1
  const defaultTitle = active.multiple ? 'Select files' : 'Select a file'

  return (
    <CustomModal
      title={active.title || defaultTitle}
      dialogClassName='ob-file-pick-modal__dialog'
      closeModal={() => void answer([])}
      buttons={[
        {
          text: 'Cancel',
          className: 'btn btn-light',
          onClick: () => void answer([]),
        },
        {
          // The count is the only confirmation that ctrl-clicking added rather than replaced.
          text: active.multiple && selected.length ? `Select (${selected.length})` : 'Select',
          className: 'btn btn-primary',
          disabled: selected.length === 0,
          onClick: () => void answer(selected),
        },
      ]}
    >
      <div className='ob-file-pick'>
        {/*
          Keyed by request: the dialog stays mounted while the queue drains, so without this the
          next request would inherit this one's folder, selection and "show all" state.
        */}
        <FileTable
          key={active.reqId}
          title={active.title || defaultTitle}
          hideTitle
          initialPath={initial.folder}
          pick={pickOptions}
        />

        {waiting > 0 && (
          <div className='text-muted mt-2' style={{fontSize: '0.85rem'}}>
            {waiting} other request{waiting > 1 ? 's' : ''} waiting for this dialog.
          </div>
        )}
      </div>
    </CustomModal>
  )
}
